/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { css } from '@emotion/react';
import { EuiPage } from './page';
import { EuiPageSideBar_Deprecated as EuiPageSideBar } from './page_side_bar';
import { EuiPageBody } from './page_body';
import { EuiPageHeader } from './page_header';
import { EuiPageContent_Deprecated as EuiPageContent, EuiPageContentBody_Deprecated as EuiPageContentBody } from './page_content';
import { EuiBottomBar } from '../bottom_bar';
import { useIsWithinBreakpoints } from '../../services';
import { EuiFlexGroup, EuiFlexItem } from '../flex';
import { useEuiPaddingSize, logicalCSS } from '../../global_styling';
export var TEMPLATES = ['default', 'centeredBody', 'centeredContent', 'empty'];
/**
 * This component has been deprecated in favor of the new
 * namespaced version. You can still import this component
 * until August 2023 by importing `as EuiPageTemplate`.
 *
 * @deprecated use EuiPageTemplate from page_template/page_template instead
 */
export var EuiPageTemplate_Deprecated = ({
  template = 'default',
  restrictWidth = true,
  grow = true,
  paddingSize = 'l',
  fullHeight,
  children,
  className,
  pageSideBar,
  pageSideBarProps,
  pageHeader,
  pageBodyProps,
  pageContentProps,
  pageContentBodyProps,
  bottomBar,
  bottomBarProps,
  minHeight = 460,
  ...rest
}) => {
  // BWC page header margin to match padding
  const headerMargin = /*#__PURE__*/css(logicalCSS('margin-bottom', useEuiPaddingSize(paddingSize)), ";label:headerMargin;");

  /**
   * Full height ~madness~ logic
   */
  const canFullHeight = useIsWithinBreakpoints(['m', 'l', 'xl']) && (template === 'default' || template === 'empty');
  const fullHeightClass = {
    'eui-fullHeight': fullHeight && canFullHeight
  };
  const yScrollClass = {
    'eui-yScroll': fullHeight && canFullHeight
  };
  if (canFullHeight && fullHeight) {
    // By using flex group it will also fix the negative margin issues for nested flex groups
    children = <EuiFlexGroup className="eui-fullHeight" gutterSize="none" direction="column" responsive={false}>
        <EuiFlexItem className={classNames({
        'eui-yScroll': fullHeight === true,
        'eui-fullHeight': fullHeight === 'noscroll'
      })} grow={true}>
          {children}
        </EuiFlexItem>
      </EuiFlexGroup>;
    pageBodyProps = {
      ...pageBodyProps,
      className: classNames(fullHeightClass, pageBodyProps?.className)
    };
    pageContentProps = {
      ...pageContentProps,
      className: classNames(yScrollClass, pageContentProps?.className)
    };
    pageContentBodyProps = {
      ...pageContentBodyProps,
      className: classNames(fullHeightClass, pageContentBodyProps?.className)
    };
  }
  const classes = classNames('euiPageTemplate', fullHeightClass, className);
  const pageStyle = {
    minHeight,
    ...rest.style
  };

  /**
   * This seems very repetitious but it's the most readable, scalable, and maintainable
   */

  switch (template) {
    /**
     * CENTERED BODY
     * The panelled content is centered
     */
    case 'centeredBody':
      return pageSideBar ? <EuiPage className={classes} paddingSize="none" grow={grow} {...rest} style={pageStyle}>
          <EuiPageSideBar sticky paddingSize={paddingSize} {...pageSideBarProps}>
            {pageSideBar}
          </EuiPageSideBar>

          <EuiPageBody paddingSize={paddingSize} {...pageBodyProps}>
            {pageHeader && <EuiPageHeader restrictWidth={restrictWidth} css={headerMargin} {...pageHeader} />}
            <EuiPageContent verticalPosition="center" horizontalPosition="center" paddingSize={paddingSize} {...pageContentProps}>
              <EuiPageContentBody restrictWidth={restrictWidth} {...pageContentBodyProps}>
                {children}
              </EuiPageContentBody>
            </EuiPageContent>
          </EuiPageBody>
        </EuiPage> : <EuiPage className={classes} paddingSize={paddingSize} grow={grow} {...rest} style={pageStyle}>
          <EuiPageBody restrictWidth={restrictWidth} {...pageBodyProps}>
            {pageHeader && <EuiPageHeader paddingSize="none" restrictWidth={false} css={headerMargin} bottomBorder {...pageHeader} />}
            {/* Extra page body to get the correct alignment and padding of the centered EuiPageContent */}
            <EuiPageBody>
              <EuiPageContent verticalPosition="center" horizontalPosition="center" paddingSize={paddingSize} {...pageContentProps}>
                <EuiPageContentBody paddingSize="none" restrictWidth={restrictWidth} {...pageContentBodyProps}>
                  {children}
                </EuiPageContentBody>
              </EuiPageContent>
            </EuiPageBody>
          </EuiPageBody>
        </EuiPage>;

    /**
     * CENTERED CONTENT
     * The content inside the panel is centered
     */
    case 'centeredContent':
      return pageSideBar ? <EuiPage className={classes} paddingSize="none" grow={grow} {...rest} style={pageStyle}>
          <EuiPageSideBar sticky paddingSize={paddingSize} {...pageSideBarProps}>
            {pageSideBar}
          </EuiPageSideBar>

          <EuiPageBody panelled paddingSize={paddingSize} {...pageBodyProps}>
            {pageHeader && <EuiPageHeader restrictWidth={restrictWidth} css={headerMargin} {...pageHeader} />}
            <EuiPageContent verticalPosition="center" horizontalPosition="center" hasShadow={false} color="subdued" paddingSize={paddingSize} {...pageContentProps}>
              <EuiPageContentBody restrictWidth={restrictWidth} {...pageContentBodyProps}>
                {children}
              </EuiPageContentBody>
            </EuiPageContent>
          </EuiPageBody>
        </EuiPage> : <EuiPage className={classes} paddingSize="none" grow={grow} {...rest} style={pageStyle}>
          <EuiPageBody {...pageBodyProps}>
            {pageHeader && <EuiPageHeader paddingSize={paddingSize} restrictWidth={restrictWidth} bottomBorder="extended" {...pageHeader} />}
            {/* Extra page content to get the correct alignment and padding of the centered EuiPageContent */}
            <EuiPageContent role={null} borderRadius="none" hasShadow={false} paddingSize={paddingSize} style={{
            display: 'flex'
          }}>
              <EuiPageContent verticalPosition="center" horizontalPosition="center" hasShadow={false} color="subdued" paddingSize={paddingSize} {...pageContentProps}>
                <EuiPageContentBody restrictWidth={restrictWidth} {...pageContentBodyProps}>
                  {children}
                </EuiPageContentBody>
              </EuiPageContent>
            </EuiPageContent>
          </EuiPageBody>
        </EuiPage>;

    /**
     * EMPTY
     * No panelling at all
     */
    case 'empty':
      return pageSideBar ? <EuiPage className={classes} paddingSize="none" grow={grow} {...rest} style={pageStyle}>
          <EuiPageSideBar sticky paddingSize={paddingSize} {...pageSideBarProps}>
            {pageSideBar}
          </EuiPageSideBar>

          <EuiPageBody paddingSize={paddingSize} {...pageBodyProps}>
            {pageHeader && <EuiPageHeader restrictWidth={restrictWidth} bottomBorder css={headerMargin} {...pageHeader} />}
            <EuiPageContent hasBorder={false} hasShadow={false} paddingSize={'none'} color={'transparent'} borderRadius={'none'} {...pageContentProps}>
              <EuiPageContentBody restrictWidth={restrictWidth} {...pageContentBodyProps}>
                {children}
              </EuiPageContentBody>
            </EuiPageContent>
          </EuiPageBody>
        </EuiPage> : <EuiPage className={classes} paddingSize={paddingSize} grow={grow} {...rest} style={pageStyle}>
          <EuiPageBody restrictWidth={restrictWidth} {...pageBodyProps}>
            {pageHeader && <EuiPageHeader paddingSize="none" restrictWidth={false} css={headerMargin} bottomBorder {...pageHeader} />}
            <EuiPageContent hasBorder={false} hasShadow={false} paddingSize={'none'} color={'transparent'} borderRadius={'none'} {...pageContentProps}>
              <EuiPageContentBody paddingSize="none" {...pageContentBodyProps}>
                {children}
              </EuiPageContentBody>
            </EuiPageContent>
          </EuiPageBody>
        </EuiPage>;

    /**
     * DEFAULT
     * Typical layout with nothing "centered"
     */
    default:
      // Only the default template can display a bottom bar
      const bottomBarNode = bottomBar ? <EuiBottomBar paddingSize={paddingSize} position={canFullHeight && fullHeight ? 'static' : 'sticky'}
      // Using uknown here because of the possible conflict with overriding props and position `sticky`
      {...bottomBarProps}>
          {/* Wrapping the contents with EuiPageContentBody allows us to match the restrictWidth to keep the contents aligned */}
          <EuiPageContentBody paddingSize={'none'} restrictWidth={restrictWidth}>
            {bottomBar}
          </EuiPageContentBody>
        </EuiBottomBar> : undefined;
      return pageSideBar ? <EuiPage className={classes} paddingSize="none" grow={grow} {...rest} style={pageStyle}>
          <EuiPageSideBar sticky paddingSize={paddingSize} {...pageSideBarProps}>
            {pageSideBar}
          </EuiPageSideBar>

          {/* The extra PageBody is to accommodate the bottom bar stretching to both sides */}
          <EuiPageBody panelled paddingSize="none" {...pageBodyProps}>
            <EuiPageBody component="div" paddingSize={paddingSize} className={pageBodyProps?.className}>
              {pageHeader && <EuiPageHeader bottomBorder restrictWidth={restrictWidth} css={headerMargin} {...pageHeader} />}
              <EuiPageContent hasShadow={false} hasBorder={false} color={'transparent'} borderRadius={'none'} paddingSize="none" {...pageContentProps}>
                <EuiPageContentBody restrictWidth={restrictWidth} {...pageContentBodyProps}>
                  {children}
                </EuiPageContentBody>
              </EuiPageContent>
            </EuiPageBody>
            {bottomBarNode}
          </EuiPageBody>
        </EuiPage> : <EuiPage className={classes} paddingSize="none" grow={grow} {...rest} style={pageStyle}>
          <EuiPageBody {...pageBodyProps}>
            {pageHeader && <EuiPageHeader restrictWidth={restrictWidth} paddingSize={paddingSize} bottomBorder="extended" {...pageHeader} />}
            <EuiPageContent hasBorder={pageHeader === undefined ? false : undefined} hasShadow={false} paddingSize={'none'} color={'plain'} borderRadius={'none'} {...pageContentProps}>
              <EuiPageContentBody restrictWidth={restrictWidth} paddingSize={paddingSize} {...pageContentBodyProps}>
                {children}
              </EuiPageContentBody>
            </EuiPageContent>
            {bottomBarNode}
          </EuiPageBody>
        </EuiPage>;
  }
};
EuiPageTemplate_Deprecated.propTypes = {
  /**
     * Choose between 3 types of templates.
     * `default`: Typical layout with nothing centered
     * `centeredBody`: The panelled content is centered
     * `centeredContent`: The content inside the panel is centered
     * `empty`: Removes the panneling of the page content
     */
  template: PropTypes.any,
  /**
     * Padding size will not get applied to the over-arching #EuiPage,
     * but will propogate through all the components to keep them in sync
     */
  paddingSize: PropTypes.oneOf(["none", "s", "m", "l"]),
  /**
     * Optionally include #EuiPageSideBar content.
     * The inclusion of this will affect the whole layout
     */
  pageSideBar: PropTypes.node,
  /**
     * Gets passed along to the #EuiPageSideBar component
     */
  pageSideBarProps: PropTypes.any,
  /**
     * Optionally include an #EuiPageHeader by passing an object of its props
     */
  pageHeader: PropTypes.shape({
    /**
       * Adjust the overall padding.
       */
    paddingSize: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
       * Sets the max-width of the page,
       * set to `true` to use the default size of `1200px`,
       * set to `false` to not restrict the width,
       * set to a number for a custom width in px,
       * set to a string for a custom width in custom measurement.
       */
    restrictWidth: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.number.isRequired, PropTypes.string.isRequired]),
    /**
       * Adds a bottom border to separate it from the content after;
       * Passing `extended` will ensure the border touches the sides of the parent container.
       */
    bottomBorder: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.oneOf(["extended"])])
  }),
  /**
     * Gets passed along to the #EuiPageBody component
     */
  pageBodyProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
       * Sets the max-width of the page,
       * set to `true` to use the default size of `1200px`,
       * set to `false` to not restrict the width,
       * set to a number for a custom width in px,
       * set to a string for a custom width in custom measurement.
       */
    restrictWidth: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.number.isRequired, PropTypes.string.isRequired]),
    /**
           * Sets the HTML element for `EuiPageBody`.
           */
    component: PropTypes.any,
    /**
           * Uses an EuiPanel as the main component instead of a plain div
           */
    panelled: PropTypes.bool,
    /**
           * Extends any extra EuiPanel props if `panelled=true`
           */
    panelProps: PropTypes.any,
    /**
           * Adjusts the padding
           */
    paddingSize: PropTypes.any
  }),
  /**
     * Gets passed along to the #EuiPageContent component
     */
  pageContentProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
       * Adds a medium shadow to the panel;
       * Only works when `color="plain"`
       */
    /**
       * Adds a medium shadow to the panel;
       * Only works when `color="plain"`
       */
    hasShadow: PropTypes.bool,
    /**
       * Adds a slight 1px border on all edges.
       * Only works when `color="plain | transparent"`
       */
    /**
       * Adds a slight 1px border on all edges.
       * Only works when `color="plain | transparent"`
       */
    hasBorder: PropTypes.bool,
    /**
       * Padding for all four sides
       */
    /**
       * Padding for all four sides
       */
    paddingSize: PropTypes.any,
    /**
       * Corner border radius
       */
    /**
       * Corner border radius
       */
    borderRadius: PropTypes.any,
    /**
       * When true the panel will grow in height to match `EuiFlexItem`
       */
    /**
       * When true the panel will grow in height to match `EuiFlexItem`
       */
    grow: PropTypes.bool,
    panelRef: PropTypes.any,
    /**
       * Background color of the panel;
       * Usually a lightened form of the brand colors
       */
    /**
       * Background color of the panel;
       * Usually a lightened form of the brand colors
       */
    color: PropTypes.any,
    element: PropTypes.oneOf(["div"]),
    verticalPosition: PropTypes.oneOf(["center"]),
    horizontalPosition: PropTypes.oneOf(["center"]),
    /**
         * There should only be one EuiPageContent per page and should contain the main contents.
         * If this is untrue, set role = `null`, or change it to match your needed aria role
         */
    role: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.oneOf([null])])
  }),
  /**
     * Gets passed along to the #EuiPageContentBody component
     */
  pageContentBodyProps: PropTypes.shape({
    /**
       * Adjust the padding.
       * When using this setting it's best to be consistent throughout all similar usages
       */
    paddingSize: PropTypes.any,
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
       * Sets the max-width of the page,
       * set to `true` to use the default size of `1200px`,
       * set to `false` to not restrict the width,
       * set to a number for a custom width in px,
       * set to a string for a custom width in custom measurement.
       */
    restrictWidth: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.number.isRequired, PropTypes.string.isRequired])
  }),
  /**
     * Adds contents inside of an EuiBottomBar.
     * Only works when `template = 'default'`
     */
  bottomBar: PropTypes.any,
  /**
     * Gets passed along to the #EuiBottomBar component if `bottomBar` has contents
     */
  bottomBarProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
         * How to position the bottom bar against its parent.
         */
    position: PropTypes.oneOfType([PropTypes.oneOf(["fixed"]), PropTypes.oneOf(["static", "sticky"]).isRequired]),
    /**
         * Whether to wrap in an EuiPortal which appends the component to the body element.
         * Only works if `position` is `fixed`.
         */
    usePortal: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
      /**
         * ReactNode to render as this component's content
         */
      children: PropTypes.node.isRequired,
      /**
         * If not specified, `EuiPortal` will insert itself
         * into the end of the `document.body` by default
         */
      insert: PropTypes.shape({
        sibling: PropTypes.any.isRequired,
        position: PropTypes.any.isRequired
      }),
      /**
         * Optional ref callback
         */
      portalRef: PropTypes.func
    }).isRequired]),
    /**
         * Whether the component should apply padding on the document body element to afford for its own displacement height.
         * Only works if `usePortal` is true and `position` is `fixed`.
         */
    affordForDisplacement: PropTypes.bool,
    /**
         * Padding applied to the bar. Default is 'm'.
         */
    paddingSize: PropTypes.oneOf(["none", "s", "m", "l"]),
    /**
         * Optional class applied to the body element on mount.
         */
    bodyClassName: PropTypes.string,
    /**
         * Customize the screen reader heading that helps users find this control. Default is 'Page level controls'.
         */
    landmarkHeading: PropTypes.string,
    /**
         * Starting vertical position when `fixed` position.
         * Offset from the top of the window when `sticky` position.
         * Has no affect on `static` positions.
         */
    top: PropTypes.any,
    /**
         * Ending horizontal position when `fixed` position.
         * Has no affect on `static` or `sticky` positions.
         */
    right: PropTypes.any,
    /**
         * Starting vertical position when `fixed` position.
         * Offset from the bottom of the window when `sticky` position.
         * Has no affect on `static` positions.
         */
    bottom: PropTypes.any,
    /**
         * Starting horizontal position when `fixed` position.
         * Has no affect on `static` or `sticky` positions.
         */
    left: PropTypes.any
  }),
  /**
     * Stretches or restricts the height to 100% of the parent;
     * `true`: scrolls the EuiPageContentBody;
     * `noscroll`: removes all scroll ability;
     * Only works when `template = 'default | empty'` and breakpoint is `m` and above
     */
  fullHeight: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.oneOf(["noscroll"])]),
  /**
     * Minimum height in which to enforce scrolling
     */
  minHeight: PropTypes.any
};
try {
  EuiPageTemplate_Deprecated.__docgenInfo = {
    tags: {
      deprecated: 'use EuiPageTemplate from page_template/page_template instead'
    },
    filePath: '/app/src/components/page/page_template.tsx',
    description: 'This component has been deprecated in favor of the new\n' + 'namespaced version. You can still import this component\n' + 'until August 2023 by importing `as EuiPageTemplate`.',
    displayName: 'EuiPageTemplate_Deprecated',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      direction: {
        defaultValue: null,
        description: 'Changes the `flex-direction` property.\n' + 'Flip to `column` when not including a sidebar.',
        name: 'direction',
        parent: {
          fileName: 'app/src/components/page/page.tsx',
          name: 'EuiPageProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page.tsx',
          name: 'EuiPageProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"row" | "column"',
          value: [{
            value: '"row"'
          }, {
            value: '"column"'
          }]
        }
      },
      grow: {
        defaultValue: {
          value: 'true'
        },
        description: 'Adds `flex-grow: 1` to the whole page for stretching to fit vertically.\n' + 'Must be wrapped inside a flexbox, preferrably with `min-height: 100vh`',
        name: 'grow',
        parent: {
          fileName: 'app/src/components/page/page.tsx',
          name: 'EuiPageProps'
        },
        declarations: [{
          fileName: 'app/src/components/page/page.tsx',
          name: 'EuiPageProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      restrictWidth: {
        defaultValue: {
          value: 'true'
        },
        description: 'Sets the max-width of the page,\n' + 'set to `true` to use the default size of `1200px`,\n' + 'set to `false` to not restrict the width,\n' + 'set to a number for a custom width in px,\n' + 'set to a string for a custom width in custom measurement.',
        name: 'restrictWidth',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/_restrict_width.ts',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string | number | boolean'
        }
      },
      template: {
        defaultValue: {
          value: 'default'
        },
        description: 'Choose between 3 types of templates.\n' + '`default`: Typical layout with nothing centered\n' + '`centeredBody`: The panelled content is centered\n' + '`centeredContent`: The content inside the panel is centered\n' + '`empty`: Removes the panneling of the page content',
        name: 'template',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"empty" | "default" | "centeredBody" | "centeredContent"',
          value: [{
            value: '"empty"'
          }, {
            value: '"default"'
          }, {
            value: '"centeredBody"'
          }, {
            value: '"centeredContent"'
          }]
        }
      },
      paddingSize: {
        defaultValue: {
          value: 'l'
        },
        description: 'Padding size will not get applied to the over-arching #EuiPage,\n' + 'but will propogate through all the components to keep them in sync',
        name: 'paddingSize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "s" | "m" | "l"',
          value: [{
            value: '"none"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      pageSideBar: {
        defaultValue: null,
        description: 'Optionally include #EuiPageSideBar content.\n' + 'The inclusion of this will affect the whole layout',
        name: 'pageSideBar',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      pageSideBarProps: {
        defaultValue: null,
        description: 'Gets passed along to the #EuiPageSideBar component',
        name: 'pageSideBarProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiPageSideBarProps_Deprecated'
        }
      },
      pageHeader: {
        defaultValue: null,
        description: 'Optionally include an #EuiPageHeader by passing an object of its props',
        name: 'pageHeader',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiPageHeaderProps'
        }
      },
      pageBodyProps: {
        defaultValue: null,
        description: 'Gets passed along to the #EuiPageBody component',
        name: 'pageBodyProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '{ children?: ReactNode; } & CommonProps & ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement> & _EuiPageRestrictWidth & { ...; }'
        }
      },
      pageContentProps: {
        defaultValue: null,
        description: 'Gets passed along to the #EuiPageContent component',
        name: 'pageContentProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiPageContentProps'
        }
      },
      pageContentBodyProps: {
        defaultValue: null,
        description: 'Gets passed along to the #EuiPageContentBody component',
        name: 'pageContentBodyProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiPageContentBodyProps'
        }
      },
      bottomBar: {
        defaultValue: null,
        description: 'Adds contents inside of an EuiBottomBar.\n' + "Only works when `template = 'default'`",
        name: 'bottomBar',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      bottomBarProps: {
        defaultValue: null,
        description: 'Gets passed along to the #EuiBottomBar component if `bottomBar` has contents',
        name: 'bottomBarProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiBottomBarProps'
        }
      },
      fullHeight: {
        defaultValue: null,
        description: 'Stretches or restricts the height to 100% of the parent;\n' + '`true`: scrolls the EuiPageContentBody;\n' + '`noscroll`: removes all scroll ability;\n' + "Only works when `template = 'default | empty'` and breakpoint is `m` and above",
        name: 'fullHeight',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean | "noscroll"'
        }
      },
      minHeight: {
        defaultValue: {
          value: '460'
        },
        description: 'Minimum height in which to enforce scrolling',
        name: 'minHeight',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/page/page_template.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'MinHeight<string | number>'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'EuiPageProps']
  };
} catch (e) {}